import ListMenu from '@rio-cloud/rio-uikit/ListMenu';
import type { ListMenuItem } from '@rio-cloud/rio-uikit/components/listMenu/ListMenuGroup';
import type { MouseEventHandler } from 'react';

import { useAppDispatch, useAppSelector } from '../../configuration/setup/hooks';
import { createRouteInfo, propagateRoutes } from '../../features/livemonitorCommunication/MapRouteRendering';
import { updateItineraryWaypoints } from '../../features/search/updateItineraryWaypoint';
import { updateWaypointsRange } from '../../features/search/updateWaypointsRange';
import type { Currency } from '../../store/facade/facadeApiV2';
import { selectVehicleCosts } from '../../store/route/routeSelectors';
import { selectSelectedItinerary } from '../../store/tour/tourSelectors';
import { type Itinerary, updateSelectedItinerary, updateTourItineraryId } from '../../store/tour/tourSlice';

interface TourAlternativesProps {
    itineraries: Itinerary[];
}

interface MenuItemsProps {
    itineraryIndex: number;
    onItemClick: MouseEventHandler<HTMLSpanElement>;
    onItemHover: MouseEventHandler<HTMLSpanElement>;
    onItemHoverLeave: MouseEventHandler<HTMLSpanElement>;
    onItemFocus: React.FocusEventHandler<HTMLButtonElement>;
    onItemBlur: React.FocusEventHandler<HTMLButtonElement>;
    onKeyDown: React.KeyboardEventHandler<HTMLButtonElement>;
    itineraries: Itinerary[];
    currency: string;
}

const menuItems = ({
    itineraryIndex,
    onItemClick,
    onItemHover,
    onItemHoverLeave,
    onItemFocus,
    onItemBlur,
    onKeyDown,
    itineraries: routes,
    currency,
}: MenuItemsProps): ListMenuItem[] => {
    const listMenu: ListMenuItem[] = [];
    listMenu.push({ navItems: [] });

    routes.forEach((itinerary, index) => {
        const routeMarkerLabel = createRouteInfo(itinerary, currency as Currency);
        listMenu[0].navItems.push({
            key: itinerary.itineraryId ?? `itinerary-${index}`,
            item: (
                // biome-ignore lint/a11y/useSemanticElements: CSS styling requires span element
                <span
                    role='button'
                    tabIndex={0}
                    data-key={index.toString()}
                    data-testid='menu-list-item'
                    className={` ${itineraryIndex === index ? 'text-medium active' : ''}`}
                    onClick={onItemClick}
                    onMouseOver={onItemHover}
                    onMouseOut={onItemHoverLeave}
                    onFocus={onItemFocus}
                    onBlur={onItemBlur}
                    onKeyDown={onKeyDown}
                >
                    <span className='rioglyph rioglyph-route padding-right-10' aria-hidden='true' />
                    {routeMarkerLabel}
                </span>
            ),
        });
    });

    return listMenu;
};

export const TourAlternatives = ({ itineraries }: TourAlternativesProps) => {
    const dispatch = useAppDispatch();
    const itineraryIndex = useAppSelector(selectSelectedItinerary) ?? 0;
    const { currency } = useAppSelector(selectVehicleCosts);

    const onItemClick = (event: React.MouseEvent) => {
        const keyString = (event.target as HTMLSpanElement).getAttribute('data-key') as string;
        const key = Number(keyString);
        dispatch(updateSelectedItinerary(key));
        dispatch(updateTourItineraryId(itineraries.at(key)?.itineraryId));
        dispatch(updateWaypointsRange(itineraries, key));
        dispatch(propagateRoutes(itineraries, key));
        dispatch(updateItineraryWaypoints);
    };

    const onItemHover = (event: React.MouseEvent) => {
        const keyString = (event.target as HTMLSpanElement).getAttribute('data-key') as string;
        const key = Number(keyString);
        dispatch(propagateRoutes(itineraries, key));
    };

    const onItemHoverLeave = (_event: React.MouseEvent) => {
        dispatch(propagateRoutes(itineraries, itineraryIndex));
    };

    const onItemFocus = (event: React.FocusEvent<HTMLButtonElement>) => {
        const keyString = (event.target as HTMLButtonElement).getAttribute('data-key') as string;
        const key = Number(keyString);
        dispatch(propagateRoutes(itineraries, key));
    };

    const onItemBlur = () => {
        dispatch(propagateRoutes(itineraries, itineraryIndex));
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            const keyString = (event.target as HTMLButtonElement).getAttribute('data-key') as string;
            const key = Number(keyString);
            dispatch(updateSelectedItinerary(key));
            dispatch(updateWaypointsRange(itineraries, key));
            dispatch(propagateRoutes(itineraries, key));
            dispatch(updateItineraryWaypoints);
        }
    };

    return (
        <div data-testid='tour-alternatives'>
            <div className='list-group' data-testid='tour-alternatives-panel'>
                <ListMenu
                    responsive={false}
                    groupClassName='padding-0'
                    menuItems={menuItems({
                        itineraryIndex,
                        onItemClick,
                        onItemHover,
                        onItemHoverLeave,
                        onItemFocus,
                        onItemBlur,
                        onKeyDown,
                        itineraries,
                        currency,
                    })}
                />
            </div>
        </div>
    );
};
